import moment from "moment";
import { createElement } from "react";
import { formatEntryDate } from "shared/hooks/helpers";

import type { ColumnAlignType } from "primereact/column";
import type { ReactNode } from "react";

export const TABLE_COLUMN_KEYS = {
  CODE: "code",
  FULL_NAME: "fullName",
  SENIORITY_DATE: "seniorityDate",
  SENIORITY_DATE_TIMESTAMP: "seniorityDateTimestamp",
  TOTAL_EARNED_DAYS: "totalEarnedDays",
  TOTAL_TAKEN_DAYS: "totalTakenDays",
  TOTAL_PROPORTIONAL_DAYS: "totalProportionalDays",
  TOTAL_AVAILABLE_DAYS: "totalAvailableDays",
  EXPIRATION_DATE: "expirationDate",
} as const;

export const TABLE_COLUMN_HEADERS = {
  [TABLE_COLUMN_KEYS.CODE]: "Código",
  [TABLE_COLUMN_KEYS.FULL_NAME]: "Nombre del colaborador",
  [TABLE_COLUMN_KEYS.SENIORITY_DATE]: "Fecha de ingreso",
  [TABLE_COLUMN_KEYS.SENIORITY_DATE_TIMESTAMP]: "Antigüedad",
  [TABLE_COLUMN_KEYS.TOTAL_EARNED_DAYS]: "Días de vacaciones ganados",
  [TABLE_COLUMN_KEYS.TOTAL_TAKEN_DAYS]: "Días disfrutados",
  [TABLE_COLUMN_KEYS.TOTAL_PROPORTIONAL_DAYS]: "Proporción del año actual",
  [TABLE_COLUMN_KEYS.TOTAL_AVAILABLE_DAYS]: "Disponibles",
  [TABLE_COLUMN_KEYS.EXPIRATION_DATE]: "Fecha de vencimiento",
} as const;

type BalancesColumnT = {
  field: string;
  header: string;
  sortable?: boolean;
  align: ColumnAlignType;
  style: Record<string, string | number>;
  bodyStyle?: Record<string, string | number>;
  frozen?: boolean;
  body?: (rowData: Record<string, unknown>) => ReactNode;
};

export const EXPORT_COLUMNS: { key: string; header: string }[] = [
  {
    key: TABLE_COLUMN_KEYS.CODE,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.CODE],
  },
  {
    key: TABLE_COLUMN_KEYS.FULL_NAME,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.FULL_NAME],
  },
  {
    key: TABLE_COLUMN_KEYS.SENIORITY_DATE,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.SENIORITY_DATE],
  },
  {
    key: TABLE_COLUMN_KEYS.SENIORITY_DATE_TIMESTAMP,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.SENIORITY_DATE_TIMESTAMP],
  },
  {
    key: TABLE_COLUMN_KEYS.TOTAL_EARNED_DAYS,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.TOTAL_EARNED_DAYS],
  },
  {
    key: TABLE_COLUMN_KEYS.TOTAL_TAKEN_DAYS,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.TOTAL_TAKEN_DAYS],
  },
  {
    key: TABLE_COLUMN_KEYS.TOTAL_PROPORTIONAL_DAYS,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.TOTAL_PROPORTIONAL_DAYS],
  },
  {
    key: TABLE_COLUMN_KEYS.TOTAL_AVAILABLE_DAYS,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.TOTAL_AVAILABLE_DAYS],
  },
];

export const BALANCES_COLUMNS: BalancesColumnT[] = [
  {
    field: TABLE_COLUMN_KEYS.CODE,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.CODE],
    sortable: true,
    align: "left",
    style: { width: "5.625rem", minWidth: "5.625rem" },
    frozen: true,
  },
  {
    field: TABLE_COLUMN_KEYS.FULL_NAME,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.FULL_NAME],
    sortable: true,
    align: "left",
    style: { width: "12.5rem", minWidth: "12.5rem", flex: 1 },
    frozen: true,
  },
  {
    field: TABLE_COLUMN_KEYS.SENIORITY_DATE,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.SENIORITY_DATE],
    sortable: true,
    align: "left",
    style: { minWidth: "8rem", flex: 1 },
    body: (rowData) =>
      moment(rowData.seniorityDate as string).format("DD/MM/YYYY") as ReactNode,
  },
  {
    field: TABLE_COLUMN_KEYS.SENIORITY_DATE_TIMESTAMP,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.SENIORITY_DATE_TIMESTAMP],
    sortable: true,
    align: "left",
    style: { minWidth: "8rem", flex: 1 },
    body: (rowData) =>
      formatEntryDate(rowData.seniorityDate as string) as ReactNode,
  },
  {
    field: TABLE_COLUMN_KEYS.TOTAL_EARNED_DAYS,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.TOTAL_EARNED_DAYS],
    sortable: true,
    align: "left",
    style: { minWidth: "15rem", flex: 1 },
  },
  {
    field: TABLE_COLUMN_KEYS.TOTAL_TAKEN_DAYS,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.TOTAL_TAKEN_DAYS],
    sortable: true,
    align: "left",
    style: { minWidth: "15rem", flex: 1 },
  },
  {
    field: TABLE_COLUMN_KEYS.TOTAL_PROPORTIONAL_DAYS,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.TOTAL_PROPORTIONAL_DAYS],
    sortable: true,
    align: "left",
    style: { minWidth: "15rem", flex: 1 },
  },
  {
    field: TABLE_COLUMN_KEYS.TOTAL_AVAILABLE_DAYS,
    header: TABLE_COLUMN_HEADERS[TABLE_COLUMN_KEYS.TOTAL_AVAILABLE_DAYS],
    sortable: true,
    align: "left",
    style: { minWidth: "15rem", flex: 1 },
    body: (rowData) => {
      const value = Number.parseFloat(rowData.totalAvailableDays as string);

      let color = "var(--Brand-Green-600)";

      if (value < 0) color = "var(--Brand-Red-600)";
      else if (value === 0) color = "var(--Brand-Neutral-900)";

      return createElement(
        "span",
        { style: { color } },
        rowData.totalAvailableDays as string,
      );
    },
  },
];
